import { useMemo } from "react";
import { formatMoney, type Country } from "@/lib/gold-data";

type Props = {
  country: Country;
  cityName: string;
  currentPrice: number;
  unitLabel: string;
  karatLabel?: string;
};

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromString(value: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function buildSeries(currentPrice: number, seedKey: string, days = 180) {
  const rand = mulberry32(seedFromString(seedKey));
  const band = 0.04;
  const points: { date: string; price: number }[] = [];
  const now = new Date();
  now.setUTCHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setUTCDate(now.getUTCDate() - i);
    const iso = d.toISOString().slice(0, 10);
    if (i === 0) {
      points.push({ date: iso, price: currentPrice });
    } else {
      const drift = ((days - i) / days - 0.5) * 0.018;
      const offset = (rand() * 2 - 1) * band + drift;
      points.push({ date: iso, price: +(currentPrice * (1 + offset)).toFixed(2) });
    }
  }
  return points;
}

export function GoldCityTrendChart({ country, cityName, currentPrice, unitLabel, karatLabel = "24K" }: Props) {
  const currency = country === "pakistan" ? "PKR" : "INR";
  const seedKey = `gold:${country}:${cityName}:${karatLabel}:${unitLabel}`;
  const points = useMemo(() => buildSeries(currentPrice, seedKey), [currentPrice, seedKey]);
  const width = 960;
  const height = 288;
  const pad = { top: 20, right: 24, bottom: 34, left: 88 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;
  const prices = points.map((point) => point.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = Math.max(max - min, 1);
  const yMin = min - range * 0.08;
  const yMax = max + range * 0.08;
  const path = points
    .map((point, index) => {
      const x = pad.left + (index / Math.max(points.length - 1, 1)) * chartW;
      const y = pad.top + ((yMax - point.price) / Math.max(yMax - yMin, 1)) * chartH;
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPath = `${path} L${pad.left + chartW},${pad.top + chartH} L${pad.left},${pad.top + chartH} Z`;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((tick) => yMin + (yMax - yMin) * tick);
  const xTicks = [0, 45, 90, 135, 179].map((index) => points[Math.min(index, points.length - 1)]);
  const gradientId = `gold-city-fill-${country}-${cityName.replace(/[^a-z0-9]/gi, "-")}-${karatLabel}`;

  return (
    <section className="mx-auto max-w-4xl px-4 pb-6">
      <h2 className="text-xl md:text-2xl font-bold mb-2">
        Last 6 months — {karatLabel} gold chart in {cityName}
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        City-wise {karatLabel} gold trend in {currency} ({unitLabel}). Today’s live price is anchored at the right edge.
      </p>
      <div className="rounded-xl border border-border bg-card p-4">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${karatLabel} gold chart in ${cityName}`} className="h-72 w-full overflow-visible">
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#d4a017" stopOpacity="0.24" />
              <stop offset="100%" stopColor="#d4a017" stopOpacity="0.03" />
            </linearGradient>
          </defs>
          {yTicks.map((tick) => {
            const y = pad.top + ((yMax - tick) / Math.max(yMax - yMin, 1)) * chartH;
            return (
              <g key={tick}>
                <line x1={pad.left} x2={pad.left + chartW} y1={y} y2={y} stroke="currentColor" className="text-border" strokeDasharray="4 5" />
                <text x={pad.left - 12} y={y + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
                  {Math.round(tick).toLocaleString(country === "pakistan" ? "en-PK" : "en-IN")}
                </text>
              </g>
            );
          })}
          {xTicks.map((tick) => {
            const index = points.indexOf(tick);
            const x = pad.left + (index / Math.max(points.length - 1, 1)) * chartW;
            return (
              <text key={tick.date} x={x} y={height - 10} textAnchor="middle" className="fill-muted-foreground text-[11px]">
                {new Date(tick.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </text>
            );
          })}
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path d={path} fill="none" stroke="#d4a017" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={pad.left + chartW} cy={pad.top + ((yMax - currentPrice) / Math.max(yMax - yMin, 1)) * chartH} r="5" fill="#d4a017" />
          <text x={pad.left} y="14" className="fill-foreground text-[12px] font-bold">
            {karatLabel} {unitLabel}: {formatMoney(currentPrice, currency)}
          </text>
        </svg>
      </div>
    </section>
  );
}